"use client";

import { type VariantProps, cva } from "class-variance-authority";
import * as React from "react";

import * as DialogPrimitive from "@radix-ui/react-dialog";

import DrawerCloseIcon from "~/hec/assets/icons/drawer-close-icon";
import { cn } from "~/lib/utils";
import { isClient } from "~/utils/common";

const Dialog = DialogPrimitive.Root;

const DialogTrigger = DialogPrimitive.Trigger;

const DialogPortal = DialogPrimitive.Portal;

const DialogClose = DialogPrimitive.Close;

// `data-[state=closed]:!pointer-events-none` — see the matching note in
// app/components/ui/dialog.tsx: if the exit animation stalls, the closed
// overlay stays in the DOM as an invisible full-screen layer that swallows
// every click. The `!` is load-bearing: Radix renders DialogPrimitive.Overlay
// with an unconditional inline `style="pointer-events: auto"` while mounted,
// which beats any non-important class — without `!important` this class is
// inert in exactly the stall it guards against. Safe at closed-state only:
// while the exit runs, Radix's body pointer-events lock already makes the
// page non-interactive. Pinned by overlay-click-through.test.tsx.
const modalOverlayVariants = cva("", {
  variants: {
    type: {
      default:
        "fixed inset-0 z-50 bg-overlay backdrop-blur-sm data-[state=closed]:!pointer-events-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      addToCart:
        "fixed h-full w-full inset-0 bottom-0 z-1400 bg-overlay backdrop-blur-sm data-[state=closed]:!pointer-events-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
    },
  },
  defaultVariants: {
    type: "default",
  },
});

type Type = "default" | "addToCart";

// Enter/exit motion for the modal CONTENT, kept independent of `type` (which
// drives the overlay). `pop` is the historical default — preserved byte-for-byte
// so quick view and every other Dialog consumer animate exactly as before.
// `soft` matches SFRA's add-to-cart modal (Bootstrap 4: ~50px slide-from-top +
// fade, NO zoom pop) — see SFCC-11675. This variant only carries the
// distinguishing TRANSFORM (slide-from-top, no zoom); the fade comes from the
// shared static base (animate-in/out + fade-in-0/fade-out-0).
//
// DURATION/EASING ARE NOT SET HERE. tailwindcss-animate's `.animate-in` hard-pins
// `animation-duration` to the Tailwind v4 default (150ms) and wins the cascade
// against the `duration-*` utility — so a `duration-[…]` here was inert (the
// modal stayed at 150ms regardless, which is *why* it felt abrupt: half of SFRA's
// 300ms). The duration + timing are pinned instead by an un-layered rule in
// app.css scoped to `[data-testid="add-to-basket-modal"]`, which beats the
// layered utility. The slide-distance utility below DOES take effect — it sets
// `--tw-enter-translate-y`, a custom prop the `enter` keyframe reads, unaffected
// by the duration cascade.
//
// NOTE: split out as a cva (not a `cn` override at the call site) because
// `cn = twMerge(clsx())` runs tailwind-merge 1.x, which doesn't recognize
// tailwindcss-animate classes — so a className override cannot strip `zoom-in-95`
// (both classes would survive and source order would decide). Selecting the
// class set here avoids that entirely.
const dialogContentAnimationVariants = cva("", {
  variants: {
    animation: {
      pop: "duration-200 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-top-[48%]",
      soft: "data-[state=closed]:slide-out-to-top-24 data-[state=open]:slide-in-from-top-24",
    },
  },
  defaultVariants: {
    animation: "pop",
  },
});

type Animation = "pop" | "soft";

interface DialogOverlayProps
  extends React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>,
    VariantProps<typeof modalOverlayVariants> {
  type?: Type;
  className?: string;
  "data-testid": string | undefined;
}

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  DialogOverlayProps
>(
  (
    { type = "default", className, "data-testid": dataTestId, ...props },
    ref,
  ) => (
    <DialogPrimitive.Overlay
      ref={ref}
      className={cn(modalOverlayVariants({ type }), className)}
      data-testid={dataTestId || "dialog-DialogOverlay"}
      {...props}
    />
  ),
);
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

interface DialogContentProps
  extends React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>,
    VariantProps<typeof modalOverlayVariants> {
  type?: Type;
  // Content enter/exit motion, independent of `type` (overlay). Defaults to the
  // historical `pop`; `soft` is SFRA-parity fade + gentle slide (SFCC-11675).
  animation?: Animation;
  dialogContainer?: HTMLElement | null;
  className?: string;
  closeIcon?: React.ReactNode;
  hideCloseBtn?: boolean;
  "data-testid": string | undefined;
}

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  DialogContentProps
>(
  (
    {
      type = "default",
      animation = "pop",
      className,
      children,
      dialogContainer = isClient ? window.document.body : null,
      closeIcon = <DrawerCloseIcon />,
      hideCloseBtn = false,
      "data-testid": dataTestId,
      ...props
    },
    ref,
  ) => (
    <DialogPortal
      container={dialogContainer}
      data-testid={dataTestId || "dialog-DialogPortal"}
    >
      <DialogOverlay
        type={type}
        data-testid={dataTestId || "dialog-DialogOverlay"}
      />
      <DialogPrimitive.Content
        ref={ref}
        // Vertical (top) slide only — no horizontal slide. In Tailwind v4 the
        // slide utilities animate `transform`, which compounds with the
        // `translate`-based centering rather than replacing it, so a left-slide
        // launches the modal from the top-left corner. Dropping it makes the
        // modal slide straight down from top-center.
        className={cn(
          // Structural + shared fade (every variant fades in/out). The
          // duration/easing + the distinguishing transform (zoom vs slide)
          // come from `dialogContentAnimationVariants`.
          // `data-[state=closed]:!pointer-events-none` — same stall guard as
          // the overlay variants above: a stalled exit leaves this Content in
          // place invisibly swallowing clicks over its footprint (the
          // add-to-basket flyout sizes it to ~92% x 90vh — the Jam-caught
          // `add-to-basket-modal` testid lands on BOTH this element and the
          // overlay). The `!` beats Radix's inline `pointer-events`. Pinned by
          // overlay-click-through.test.tsx.
          "fixed left-[50%] top-[50%] z-50 grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border bg-background p-6 shadow-lg data-[state=closed]:!pointer-events-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 sm:rounded-lg",
          dialogContentAnimationVariants({ animation }),
          className,
        )}
        data-testid={dataTestId || "dialog-DialogContent"}
        {...props}
      >
        {children}
        {hideCloseBtn ? (
          ""
        ) : (
          <DialogPrimitive.Close className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground">
            {closeIcon}
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Content>
    </DialogPortal>
  ),
);
DialogContent.displayName = DialogPrimitive.Content.displayName;

const DialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col space-y-1.5 text-center sm:text-left",
      className,
    )}
    {...props}
  />
);
DialogHeader.displayName = "DialogHeader";

const DialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2",
      className,
    )}
    {...props}
  />
);
DialogFooter.displayName = "DialogFooter";

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title> & {
    className?: string;
    "data-testid"?: string | undefined;
  }
>(({ className, "data-testid": dataTestId, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn("text-lg font-medium leading-none tracking-tight", className)}
    data-testid={dataTestId || "dialog-DialogTitle"}
    {...props}
  />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description> & {
    className?: string;
    "data-testid"?: string | undefined;
  }
>(({ className, "data-testid": dataTestId, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    data-testid={dataTestId || "dialog-DialogDescription"}
    {...props}
  />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

export {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
};
